import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  Settings2,
} from 'lucide-react';
import { useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { getSpecBySlug } from '~/lib/db/queries/specs';
import {
  getProductTemplate,
  PRODUCT_CATEGORY_LABELS,
} from '~/lib/product-templates';
import { COMPLEXITY_COLORS } from '~/lib/utils';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  if (!loaderData) return [{ title: 'Product — Specly' }];
  const { product } = loaderData;
  return [
    { title: `${product.name} Spec — Specly` },
    {
      name: 'description',
      content: `See the feature specs behind ${product.name}. ${product.tagline}.`,
    },
  ];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const product = getProductTemplate(params.slug!);
  if (!product) throw new Response('Not Found', { status: 404 });

  const spec = await getSpecBySlug(product.specSlug);
  if (!spec) throw new Response('Spec not found', { status: 404 });

  return { product, spec };
}

export default function ProductPage() {
  const { product, spec } = useLoaderData<typeof loader>();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/products"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="size-4" />
        All products
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-muted-foreground">
            {PRODUCT_CATEGORY_LABELS[product.category] ?? product.category}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-sm text-muted-foreground">
            Based on:{' '}
            <Link
              to={`/specs/${spec.slug}`}
              className="text-primary hover:text-primary/80 font-medium"
            >
              {spec.name}
            </Link>
          </span>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
            <p className="text-lg text-muted-foreground">{product.tagline}</p>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              {product.description}
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button
              size="sm"
              render={<Link to={`/specs/${spec.slug}/build`} />}
            >
              <Settings2 data-icon="inline-start" />
              Build this
              <ArrowRight data-icon="inline-end" />
            </Button>
          </div>
        </div>
      </div>

      {/* Insight banner */}
      <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4 mb-8">
        <Info className="size-4 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          These are the feature specs that describe how{' '}
          <strong className="text-foreground">{product.name}</strong> is built.
          Each card shows the spec module and notes on how{' '}
          <strong className="text-foreground">{product.name}</strong> implements
          it. Hit <strong className="text-foreground">Build this</strong> to
          customise options and export as an AI prompt.
        </p>
      </div>

      {/* Feature list with product-specific notes */}
      <h2 className="text-lg font-semibold mb-4">
        Features
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          ({spec.specFeatures.length})
        </span>
      </h2>

      <div className="flex flex-col gap-3 mb-12">
        {spec.specFeatures.map((sf) => (
          <ProductFeatureCard
            key={sf.featureId}
            feature={sf.feature}
            isRequired={sf.isRequired}
            productNote={product.featureNotes[sf.feature.slug]}
            productName={product.name}
          />
        ))}
      </div>

      {/* CTA */}
      <div className="bg-card border rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-semibold mb-1">
            Build your own {product.name}-like app
          </p>
          <p className="text-sm text-muted-foreground">
            Customise features, pick lightweight or enterprise options, and
            export a prompt for any AI builder.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            render={<Link to={`/specs/${spec.slug}`} />}
          >
            <ExternalLink data-icon="inline-start" />
            View spec
          </Button>
          <Button size="sm" render={<Link to={`/specs/${spec.slug}/build`} />}>
            <Settings2 data-icon="inline-start" />
            Build this
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ProductFeatureCardProps {
  feature: {
    slug: string;
    name: string;
    description: string;
    complexity: string;
  };
  isRequired: boolean;
  productNote: string | undefined;
  productName: string;
}

function ProductFeatureCard({
  feature,
  isRequired,
  productNote,
  productName,
}: ProductFeatureCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold">{feature.name}</span>
            {isRequired && <Badge variant="secondary">Required</Badge>}
            <span
              className={`text-xs font-medium border rounded-full px-2 py-0.5 ${COMPLEXITY_COLORS[feature.complexity as 'low' | 'medium' | 'high']}`}
            >
              {feature.complexity} complexity
            </span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {feature.description}
          </p>
        </div>
        <div className="flex-shrink-0 text-muted-foreground">
          {open ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-border px-5 py-4 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{feature.description}</p>

          {productNote && (
            <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0 uppercase tracking-wide">
                {productName}
              </span>
              <p className="text-sm text-muted-foreground">{productNote}</p>
            </div>
          )}

          <Link
            to={`/features/${feature.slug}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
          >
            <ExternalLink className="size-3.5" />
            View full feature spec
          </Link>
        </div>
      )}
    </Card>
  );
}
